/**
 * Headers Middleware for Web App
 *
 * Adds consistent headers to all API requests.
 * These headers provide context about the request origin, client version, and device info.
 */

import { UAParser } from 'ua-parser-js'
import { v4 as uuidv4 } from 'uuid'
import type { ApiRequestOptions } from '../../../../openapi/core/ApiRequestOptions'
import { posthogClient } from '../../../../services/posthog/posthog.service'
import { getDeviceId } from '../device-id'

export enum HeaderKey {
  REQUEST_ID = 'X-Request-Id',
  DEVICE_ID = 'X-Device-Id',
  PLATFORM = 'X-Platform',
  OS_VERSION = 'X-OS-Version',
  DEVICE_MODEL = 'X-Device-Model',
  CLIENT_VERSION = 'X-Client-Version',
  CLIENT_BUILD_NUMBER = 'X-Client-Build-Number',
  USER_AGENT = 'X-User-Agent',
  // posthog-js session id: lets server-side PostHog issues and logs link to
  // this browser's recording (the server reads it into its request context).
  POSTHOG_SESSION_ID = 'X-PostHog-Session-Id',
}

interface DeviceInfo {
  deviceId: string
  platform: string
  osVersion: string
  deviceModel: string
  userAgent: string
}

interface ClientInfo {
  version: string
  buildNumber: string
}

/**
 * Extracts device information using ua-parser-js
 */
function getDeviceInfo(): DeviceInfo {
  const parser = new UAParser()
  const result = parser.getResult()

  // Format OS version
  const osName = result.os.name || 'Unknown'
  const osVersion = result.os.version ? `${osName} ${result.os.version}` : osName

  // Format browser/device model - using browser name and version
  const browserName = result.browser.name || 'Unknown Browser'
  const browserVersion = result.browser.version
  const deviceModel = browserVersion ? `${browserName} ${browserVersion}` : browserName

  return {
    deviceId: getDeviceId(),
    platform: 'web',
    osVersion,
    deviceModel,
    userAgent: navigator.userAgent,
  }
}

/**
 * Gets client version information from package.json
 * Note: In production, this would typically come from environment variables
 * or be injected during build time
 */
function getClientInfo(): ClientInfo {
  // These are injected during build time
  const version = import.meta.env.VITE_APP_VERSION
  const buildNumber = import.meta.env.VITE_APP_BUILD_NUMBER

  return {
    version,
    buildNumber,
  }
}

/**
 * Creates headers object with all the custom headers
 */
export function createApiHeaders(): Record<string, string> {
  const deviceInfo = getDeviceInfo()
  const clientInfo = getClientInfo()

  return {
    [HeaderKey.REQUEST_ID]: uuidv4(),
    [HeaderKey.DEVICE_ID]: deviceInfo.deviceId,
    [HeaderKey.PLATFORM]: deviceInfo.platform,
    [HeaderKey.OS_VERSION]: deviceInfo.osVersion,
    [HeaderKey.DEVICE_MODEL]: deviceInfo.deviceModel,
    [HeaderKey.CLIENT_VERSION]: clientInfo.version,
    [HeaderKey.CLIENT_BUILD_NUMBER]: clientInfo.buildNumber,
    [HeaderKey.USER_AGENT]: `Taka Web ${clientInfo.version} (${clientInfo.buildNumber})`,
    // Opt into cookie-based refresh-token storage on the server. Native clients omit
    // this header and continue to receive refresh tokens in the response body.
    'X-Client-Type': 'web',
    ...posthogSessionHeader(),
  }
}

function posthogSessionHeader(): Record<string, string> {
  const sessionId = posthogClient.getSessionId()
  return sessionId ? { [HeaderKey.POSTHOG_SESSION_ID]: sessionId } : {}
}

/**
 * Headers resolver function for OpenAPI client
 * This function is called for each request to generate fresh headers
 */

export const headersResolver = async (
  _options: ApiRequestOptions
): Promise<Record<string, string>> => {
  return createApiHeaders()
}
